import { useState, useEffect, createContext, useContext } from "react";
import { getBreakpointFromWidth, type Breakpoint } from "./useBreakpoint";

export type Orientation = "portrait" | "landscape" | "unknown";
export type PointerType = "fine" | "coarse" | "none";
export type ScreenSizeCategory = Breakpoint;

type DeviceConnectionInfo = {
  effectiveType?: string;
  type?: string;
  saveData?: boolean;
  addEventListener?: (type: "change", listener: EventListenerOrEventListenerObject) => void;
  removeEventListener?: (type: "change", listener: EventListenerOrEventListenerObject) => void;
};

type NavigatorWithConnection = Navigator & {
  connection?: DeviceConnectionInfo;
  mozConnection?: DeviceConnectionInfo;
  webkitConnection?: DeviceConnectionInfo;
};

type WindowWithOrientation = Window & {
  orientation?: number;
};

type MediaQueryListWithLegacyListeners = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

export const addMediaQueryChangeListener = (mql: MediaQueryList, listener: (event: MediaQueryListEvent) => void) => {
  const list = mql as MediaQueryListWithLegacyListeners;
  if (typeof list.addEventListener === "function") {
    list.addEventListener("change", listener);
    return;
  }

  list.addListener?.(listener);
};

export const removeMediaQueryChangeListener = (mql: MediaQueryList, listener: (event: MediaQueryListEvent) => void) => {
  const list = mql as MediaQueryListWithLegacyListeners;
  if (typeof list.removeEventListener === "function") {
    list.removeEventListener("change", listener);
    return;
  }

  list.removeListener?.(listener);
};

export interface DeviceInfo {
  // Touch & Input
  isTouch: boolean; // Primary input is touch
  isMouse: boolean; // Fine pointer (mouse/trackpad)
  canHover: boolean; // Device supports hover
  pointerType: PointerType; // fine | coarse | none
  hasMultiTouch: boolean; // Supports multi-touch

  // Orientation
  orientation: Orientation; // portrait | landscape | unknown
  orientationAngle: number; // 0 | 90 | -90 | 180

  // Screen & Display
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number; // 1 = standard, 2 = retina, 3 = super-retina
  isRetina: boolean; // devicePixelRatio >= 2
  screenCategory: ScreenSizeCategory; // xs/sm/md/lg/xl/2xl

  // Accessibility & Preferences
  prefersReducedMotion: boolean;
  prefersDarkMode: boolean;
  prefersHighContrast: boolean;
  prefersReducedTransparency: boolean;

  // Connectivity
  isOnline: boolean;
  connectionType: string | null; // "4g" | "wifi" | "ethernet" | etc.
  saveData: boolean; // User has data-saver enabled

  // Platform hints (UA-based, best-effort)
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isStandalone: boolean; // Running as installed PWA
}

function getOrientation(): { orientation: Orientation; angle: number } {
  if (typeof screen !== "undefined" && screen.orientation) {
    const type = screen.orientation.type;
    const angle = screen.orientation.angle ?? 0;
    const orientation: Orientation = type.includes("portrait") ? "portrait" : type.includes("landscape") ? "landscape" : "unknown";
    return { orientation, angle };
  }
  if (typeof window !== "undefined") {
    const angle = (window as WindowWithOrientation).orientation;
    if (typeof angle === "number" && Number.isFinite(angle)) {
      return {
        orientation: Math.abs(angle) === 90 ? "landscape" : "portrait",
        angle,
      };
    }
  }
  return { orientation: "unknown", angle: 0 };
}

export function getNavigatorConnection(): DeviceConnectionInfo | undefined {
  const browserNavigator = navigator as NavigatorWithConnection;
  return browserNavigator.connection ?? browserNavigator.mozConnection ?? browserNavigator.webkitConnection;
}

function getConnectionInfo(): {
  connectionType: string | null;
  saveData: boolean;
} {
  const conn = getNavigatorConnection();
  if (!conn) return { connectionType: null, saveData: false };
  return {
    connectionType: conn.effectiveType ?? conn.type ?? null,
    saveData: conn.saveData ?? false,
  };
}

function getPlatformHints(viewport: number): {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
} {
  // Prefer media queries over UA sniffing
  const coarsePointer = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const noHover = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

  const isMobile = coarsePointer && noHover && viewport < 768;
  const isTablet = coarsePointer && noHover && viewport >= 768;
  const isDesktop = !coarsePointer;
  return { isMobile, isTablet, isDesktop };
}

export function getDeviceSnapshot(): DeviceInfo {
  if (typeof window === "undefined") {
    // SSR fallback
    return {
      isTouch: false,
      isMouse: true,
      canHover: true,
      pointerType: "fine",
      hasMultiTouch: false,
      orientation: "unknown",
      orientationAngle: 0,
      screenWidth: 0,
      screenHeight: 0,
      viewportWidth: 0,
      viewportHeight: 0,
      devicePixelRatio: 1,
      isRetina: false,
      screenCategory: "xs",
      prefersReducedMotion: false,
      prefersDarkMode: false,
      prefersHighContrast: false,
      prefersReducedTransparency: false,
      isOnline: true,
      connectionType: null,
      saveData: false,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isStandalone: false,
    };
  }

  const mq = (q: string) => window.matchMedia(q).matches;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const dpr = window.devicePixelRatio ?? 1;
  const { orientation, angle } = getOrientation();
  const { connectionType, saveData } = getConnectionInfo();
  const { isMobile, isTablet, isDesktop } = getPlatformHints(vw);

  const pointerFine = mq("(pointer: fine)");
  const pointerCoarse = mq("(pointer: coarse)");
  const pointerType: PointerType = pointerFine ? "fine" : pointerCoarse ? "coarse" : "none";

  return {
    isTouch: pointerCoarse || navigator.maxTouchPoints > 0,
    isMouse: pointerFine,
    canHover: mq("(hover: hover)"),
    pointerType,
    hasMultiTouch: navigator.maxTouchPoints > 1,

    orientation,
    orientationAngle: angle,

    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: vw,
    viewportHeight: vh,
    devicePixelRatio: dpr,
    isRetina: dpr >= 2,
    screenCategory: getBreakpointFromWidth(vw),

    prefersReducedMotion: mq("(prefers-reduced-motion: reduce)"),
    prefersDarkMode: mq("(prefers-color-scheme: dark)"),
    prefersHighContrast: mq("(prefers-contrast: more)"),
    prefersReducedTransparency: mq("(prefers-reduced-transparency: reduce)"),

    isOnline: navigator.onLine,
    connectionType,
    saveData,

    isMobile,
    isTablet,
    isDesktop,
    isStandalone: mq("(display-mode: standalone)"),
  };
}

/**
 * Context populated by DeviceProvider. When the provider is present,
 * useDevice reads from here instead of registering its own listeners.
 */
export const DeviceContext = createContext<DeviceInfo | null>(null);

/**
 * useDevice — returns rich device/environment information useful for
 * making UI adaptations (layout, motion, pointer interaction, etc.)
 *
 * All values update reactively when the user rotates their device,
 * resizes the window, goes offline, or changes system preferences.
 *
 * Mount DeviceProvider near the root so all useDevice() calls share a
 * single set of event listeners instead of each registering their own.
 */
export function useDevice(): DeviceInfo {
  const ctx = useContext(DeviceContext);
  const hasProvider = ctx !== null;

  // Standalone state — only drives the return value when no provider is mounted.
  // useState/useEffect must always be called (Rules of Hooks).
  const [info, setInfo] = useState<DeviceInfo>(getDeviceSnapshot);

  useEffect(() => {
    if (hasProvider || typeof window === "undefined") {
      return;
    }

    const update = () => setInfo(getDeviceSnapshot());

    // Resize / orientation
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    if (screen.orientation) {
      screen.orientation.addEventListener("change", update);
    }

    // Online / offline
    window.addEventListener("online", update);
    window.addEventListener("offline", update);

    // Network info
    const conn = getNavigatorConnection();
    if (conn?.addEventListener) {
      conn.addEventListener("change", update);
    }

    // Media query listeners
    const queries = [
      "(pointer: fine)",
      "(pointer: coarse)",
      "(hover: hover)",
      "(prefers-reduced-motion: reduce)",
      "(prefers-color-scheme: dark)",
      "(prefers-contrast: more)",
      "(prefers-reduced-transparency: reduce)",
      "(display-mode: standalone)",
    ].map((q) => {
      const mql = window.matchMedia(q);
      addMediaQueryChangeListener(mql, update);
      return mql;
    });

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      screen.orientation?.removeEventListener("change", update);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      if (conn?.removeEventListener) {
        conn.removeEventListener("change", update);
      }
      queries.forEach((mql) => removeMediaQueryChangeListener(mql, update));
    };
  }, [hasProvider]);

  return ctx ?? info;
}

export default useDevice;
